import { useCallback, useRef, useState, type ChangeEvent, type RefObject } from 'react'
import type { DataTableRef } from '@/components/tables'
import { apiClient } from '@/lib/api/client'
import { downloadCSVTemplate, exportToCSV } from '@/lib/utils/csvUtils'
import {
  getAssetCatalogCsvModule,
  type AssetCatalogCsvModule,
} from '@/lib/utils/assetCatalogCsv'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type ToastFns = ReturnType<typeof useToastNotification>

type Options = {
  module: AssetCatalogCsvModule
  apiBase: string
  tableRef: RefObject<DataTableRef | null>
  showError: ToastFns['showError']
  showSuccess: ToastFns['showSuccess']
  showWarning: ToastFns['showWarning']
  /** Label for toast messages, e.g. "categories" */
  resourceLabel?: string
}

/**
 * Template / Import / Export handlers for Manage Asset Catalog list pages.
 * Export uses the active All list only (not trash), per product plan.
 */
export function useAssetCatalogCsvActions({
  module,
  apiBase,
  tableRef,
  showError,
  showSuccess,
  showWarning,
  resourceLabel,
}: Options) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const csv = getAssetCatalogCsvModule(module)
  const label = resourceLabel ?? module.replace(/-/g, ' ')

  const handleDownloadTemplate = useCallback(() => {
    downloadCSVTemplate({
      csvHeaders: [...csv.headers],
      exampleRow: [...csv.exampleRow],
      comments: [...csv.comments],
      filename: csv.filename,
      showError,
      showSuccess,
    })
  }, [csv, showError, showSuccess])

  const handleFileUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      setIsImporting(true)
      try {
        const form = new FormData()
        form.append('file', file)
        const { data } = await apiClient.post<{
          data: { imported: number; updated?: number; errors: string[] }
        }>(`${apiBase}/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const d = data?.data ?? { imported: 0, updated: 0, errors: [] }
        const parts: string[] = []
        if (d.imported > 0) parts.push(`Created ${d.imported}`)
        if ((d.updated ?? 0) > 0) parts.push(`Updated ${d.updated}`)
        showSuccess(
          'Success',
          parts.length > 0 ? `${parts.join(', ')} ${label}` : 'Import completed'
        )
        if (d.errors?.length) {
          showWarning(
            d.errors.slice(0, 3).join('; ') + (d.errors.length > 3 ? '...' : '')
          )
        }
        tableRef.current?.reload()
      } catch (err: unknown) {
        showError(err)
      } finally {
        setIsImporting(false)
        e.target.value = ''
      }
    },
    [apiBase, label, showError, showSuccess, showWarning, tableRef]
  )

  const handleExportData = useCallback(async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: apiBase,
        selectedIds: [],
        activeTab: 'all',
        searchQuery: '',
        csvHeaders: [...csv.headers],
        dataMapper: (item: Record<string, unknown>) => csv.mapRow(item),
        filename: `${module.replace(/-/g, '_')}_export.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }, [apiBase, csv, module, showError, showSuccess, showWarning])

  return {
    fileInputRef,
    isExporting,
    isImporting,
    handleDownloadTemplate,
    handleFileUpload,
    handleExportData,
  }
}
